import { gameData } from '../data/gameData.ts';
import { num } from '../model/unityProps.ts';

/**
 * Friendly names, palette categories and editable transform parts of the game's prefabs (by prefab name).
 * - position: only moves (collectables, spawn points)
 * - rotate: position + rotation
 * - zone: position + size (the root scale is the zone size)
 * - full: position, rotation and scale
 */
export type TransformKind = 'position' | 'rotate' | 'zone' | 'full';

export interface PrefabInfo {
  label: string;
  category: string;
  transform?: TransformKind;
  /** internal prefabs the level designer never places by hand */
  hidden?: boolean;
}

export const CATEGORIES = ['Collectables', 'Powerups', 'Switches & triggers', 'Platforms & blocks', 'Hazards & enemies', 'Physics objects', 'Level', 'Other'];

const P = (label: string, category: string, transform: TransformKind = 'full', hidden = false): PrefabInfo => ({ label, category, transform, hidden });

const INFO: Record<string, PrefabInfo> = {
  Coin: P('Coin', 'Collectables', 'position'),
  Gem: P('Gem', 'Collectables', 'position'),
  'Star Coin': P('Star coin', 'Collectables', 'position'),
  'SECRET CACTUS': P('Secret cactus', 'Collectables', 'position'),
  ReDash: P('Dash refill', 'Collectables', 'position'),
  ForceDash: P('Force dash', 'Collectables', 'position'),
  Repack: P('Jetpack refill', 'Collectables', 'position'),
  BlockDudePowerup: P('Block Dude powerup', 'Powerups', 'position'),
  BigDudePowerup: P('Big Dude powerup', 'Powerups', 'position'),
  DoubleDashPowerup: P('Double dash powerup', 'Powerups', 'position'),
  JetpackPowerup: P('Jetpack powerup', 'Powerups', 'position'),
  Switch: P('Switch', 'Switches & triggers', 'rotate'),
  'Switch Blue Variant': P('Switch (blue)', 'Switches & triggers', 'rotate'),
  ZoneActivator: P('Trigger zone', 'Switches & triggers', 'zone'),
  CameraPreviewZone: P('Camera preview zone', 'Switches & triggers', 'zone'),
  ControlSwitch: P('Control panel', 'Switches & triggers', 'rotate'),
  TouchBlock: P('Touch block', 'Switches & triggers', 'position'),
  'Moving Platform': P('Moving platform (prefab)', 'Platforms & blocks'),
  'Moving Platform With Zone Activator': P('Moving platform with zone', 'Platforms & blocks'),
  'Horizontal Door': P('Door (horizontal)', 'Platforms & blocks'),
  'Vertical Door': P('Door (vertical)', 'Platforms & blocks'),
  Door: P('Door', 'Platforms & blocks', 'rotate'),
  FadeOutBlock: P('Fade-out block (prefab)', 'Platforms & blocks'),
  'Rotating Spike Block': P('Rotating spike block', 'Platforms & blocks'),
  TickTockPlatform: P('Tick-tock platform', 'Platforms & blocks', 'rotate'),
  'Crumble Platform': P('Crumble platform', 'Platforms & blocks', 'rotate'),
  'Crumble Platform Single': P('Crumble block', 'Platforms & blocks', 'rotate'),
  DashPlatform: P('Dash platform', 'Platforms & blocks', 'rotate'),
  ColorMatchBlock: P('Colour match block', 'Platforms & blocks', 'rotate'),
  BodyControlFull: P('Controllable platform', 'Platforms & blocks'),
  LauncherBase: P('Launcher', 'Platforms & blocks', 'rotate'),
  SpikeyBoi: P('Spikey patroller', 'Hazards & enemies', 'position'),
  SpikeBall: P('Spike ball', 'Hazards & enemies', 'position'),
  'spikey ball launcher': P('Spike ball launcher', 'Hazards & enemies', 'rotate'),
  'spikey ball ender': P('Spike ball catcher', 'Hazards & enemies', 'zone'),
  CannonLeft: P('Cannon (left)', 'Hazards & enemies', 'rotate'),
  CannonRight: P('Cannon (right)', 'Hazards & enemies', 'rotate'),
  CannonUp: P('Cannon (up)', 'Hazards & enemies', 'rotate'),
  CannonDown: P('Cannon (down)', 'Hazards & enemies', 'rotate'),
  CannonUpRight: P('Cannon (diagonal)', 'Hazards & enemies', 'rotate'),
  Laser: P('Laser', 'Hazards & enemies', 'rotate'),
  DeathZone: P('Death zone', 'Hazards & enemies', 'zone'),
  Marble: P('Marble', 'Physics objects', 'position'),
  'Moveable Crate 1': P('Crate', 'Physics objects', 'rotate'),
  'Moveable Crate 2': P('Crate (2)', 'Physics objects', 'rotate'),
  'Moveable Rock 1': P('Rock', 'Physics objects', 'rotate'),
  'Moveable Rock 2': P('Rock (2)', 'Physics objects', 'rotate'),
  'Moveable Block': P('Pushable block', 'Physics objects', 'rotate'),
  Volleyball: P('Volleyball', 'Physics objects', 'position'),
  PlayerSpawnPoint: P('Player spawn', 'Level', 'position'),
  Checkpoint: P('Checkpoint', 'Level', 'position'),
  // internal: placed by the level template or spawned at runtime
  NecessaryLevelComponents: P('Level UI & network', 'Other', 'full', true),
  LevelScripts: P('Level scripts', 'Other', 'full', true),
  'Main Camera': P('Main camera', 'Other', 'full', true),
  'Player Prefab Networked': P('Player', 'Other', 'full', true),
  DeadQuarters: P('Death debris', 'Other', 'full', true),
  SpriteExploder: P('Sprite exploder', 'Other', 'full', true),
  TheString: P('The string', 'Other', 'full', true),
  Cannonball: P('Cannonball', 'Other', 'full', true),
  BluePlasma: P('Blue plasma', 'Other', 'full', true),
  Jetpack: P('Jetpack', 'Other', 'full', true),
  'Launcher - maybe dont use anymore': P('Old launcher', 'Other', 'full', true),
  'Gem Shard Group (unpack not completely)': P('Star coin group', 'Collectables', 'full', true),
  PlasmaCannon: P('Plasma cannon', 'Hazards & enemies', 'rotate', true),
  Spikes: P('Spikes (old)', 'Hazards & enemies', 'full', true),
};

export function prefabInfo(guid: string | null | undefined): PrefabInfo {
  if (!guid) return { label: 'Custom object', category: 'Other', transform: 'full' };
  const p = gameData().prefabs[guid];
  if (!p) return { label: 'Missing prefab', category: 'Other', transform: 'full' };
  if (p.path.includes('/Parallax/')) return { label: p.name, category: 'Other', transform: 'full', hidden: true };
  return INFO[p.name] ?? { label: p.name, category: 'Other', transform: 'full' };
}

export function prefabLabel(guid: string | null | undefined): string {
  return prefabInfo(guid).label;
}

/**
 * Root facing a newly placed object should start with.
 * CannonLeft (and the spike-ball launcher) are a horizontal mirror: the prefab root
 * scale X is -1. CannonDown is the upward cannon rotated 180°. Placing at scale 1 and
 * rotation 0 throws that away, so the left cannon looks and fires to the right.
 * Zone size stays on the placed object; only a mirror sign is kept for those.
 */
export function placementPose(guid: string | null | undefined): { rotation: number; scale: [number, number] } {
  const neutral = { rotation: 0, scale: [1, 1] as [number, number] };
  if (!guid) return neutral;
  const def = gameData().prefabs[guid];
  const root = def?.gos[0];
  if (!def || !root) return neutral;
  let data: { m_LocalScale?: { x?: string; y?: string }; m_LocalRotation?: { x?: string; y?: string; z?: string; w?: string } } | null = null;
  for (const cid of root.comps) {
    const n = def.nodes[cid];
    if (n && (n.type === 'Transform' || n.type === 'RectTransform')) data = n.data;
  }
  if (!data) return neutral;
  const s = data.m_LocalScale ?? {};
  const q = data.m_LocalRotation ?? {};
  const qx = num(q.x), qy = num(q.y), qz = num(q.z), qw = num(q.w, 1);
  const angle = (Math.atan2(2 * (qw * qz + qx * qy), 1 - 2 * (qy * qy + qz * qz)) * 180) / Math.PI;
  const rotation = Math.abs(angle) < 0.05 ? 0 : Math.round(angle * 1000) / 1000;
  const zone = prefabInfo(guid).transform === 'zone';
  const axis = (v: number): number => {
    const sign = v < 0 ? -1 : 1;
    if (zone) return sign;
    if (Math.abs(Math.abs(v) - 1) < 1e-3) return sign;
    if (v < 0) return v;
    return 1;
  };
  const scale: [number, number] = [axis(num(s.x, 1)), axis(num(s.y, 1))];
  if (!rotation && scale[0] === 1 && scale[1] === 1) return neutral;
  return { rotation, scale };
}

/** Palette entries grouped by category, in category order. */
export function paletteGroups(showHidden: boolean, query = ''): [string, string[]][] {
  const gd = gameData();
  const q = query.trim().toLowerCase();
  const groups = new Map<string, string[]>();
  for (const guids of Object.values(gd.project.prefabGroups)) {
    for (const g of guids) {
      const p = gd.prefabs[g];
      if (!p) continue;
      const info = prefabInfo(g);
      if (info.hidden && !showHidden) continue;
      if (q && !info.label.toLowerCase().includes(q) && !p.name.toLowerCase().includes(q)) continue;
      const cat = info.hidden ? 'Internal' : info.category;
      if (!groups.has(cat)) groups.set(cat, []);
      if (!groups.get(cat)!.includes(g)) groups.get(cat)!.push(g);
    }
  }
  const order = [...CATEGORIES, 'Internal'];
  return [...groups.entries()]
    .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
    .map(([c, l]) => [c, l.sort((a, b) => prefabLabel(a).localeCompare(prefabLabel(b)))]);
}
